import React from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  loading?: 'lazy' | 'eager';
}

/**
 * Gets the WebP version of an image path if it exists
 * @param imagePath - The original image path
 * @returns An object with webp and fallback paths
 */
const getImagePaths = (imagePath: string) => {
  if (!imagePath) return { webp: '', fallback: imagePath };
  
  // Skip for SVG images or external URLs
  if (imagePath.endsWith('.svg') || imagePath.startsWith('http')) {
    return { webp: '', fallback: imagePath };
  }
  
  // For paths that already reference webp, just return the path
  if (imagePath.endsWith('.webp')) {
    return { webp: imagePath, fallback: imagePath };
  }
  
  const extension = imagePath.split('.').pop() || '';
  const basePath = imagePath.substring(0, imagePath.lastIndexOf('.'));
  
  // For images in the photos directory, get the corresponding WebP version
  if (imagePath.startsWith('/photos/')) {
    // Check if it's in a subdirectory
    const filename = imagePath.split('/').pop() || '';
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    
    // The WebP path includes the photos directory in the path
    const webpPath = `/photos/webp/${nameWithoutExt}.webp`;
    
    // For milestone2 image, use more optimized versions
    if (nameWithoutExt === 'sm23-milestone2' || webpPath.includes('sm23-milestone2')) {
      return {
        webp: webpPath,
        fallback: imagePath,
        srcset: [
          { src: `/photos/webp/sm23-milestone2-400w.webp`, width: 400 },
          { src: `/photos/webp/sm23-milestone2-800w.webp`, width: 800 },
          { src: webpPath, width: 1200 }
        ]
      };
    }
    
    return { webp: webpPath, fallback: imagePath };
  }
  
  // For images in the sponsors directory
  if (imagePath.startsWith('/images/sponsors/')) {
    const filename = imagePath.split('/').pop() || '';
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const webpPath = `/images/webp/sponsors/${nameWithoutExt}.webp`;
    return { webp: webpPath, fallback: imagePath };
  }
  
  return { webp: '', fallback: imagePath };
};

const OptimizedImage: React.FC<OptimizedImageProps> = ({ 
  src, 
  alt, 
  className = '', 
  width, 
  height, 
  priority = false,
  loading = 'lazy',
  ...rest 
}) => {
  const { webp, fallback, srcset } = getImagePaths(src);
  
  // If WebP version is available, use picture element with fallback
  if (webp) {
    return (
      <picture>
        {srcset && (
          <source 
            srcSet={srcset.map(({ src, width }) => `${src} ${width}w`).join(', ')}
            type="image/webp"
            sizes="(max-width: 400px) 400px, (max-width: 800px) 800px, 1200px"
          />
        )}
        <source srcSet={webp} type="image/webp" />
        <img 
          src={fallback}
          alt={alt}
          className={className}
          width={width}
          height={height}
          loading={priority ? 'eager' : loading}
          decoding="async"
          fetchPriority={priority ? "high" : "auto"}
          {...rest}
        />
      </picture>
    );
  }
  
  // Otherwise, just render the image
  return (
    <img 
      src={src}
      alt={alt}
      className={className}
      width={width}
      height={height}
      loading={priority ? 'eager' : loading}
      decoding="async"
      fetchPriority={priority ? "high" : "auto"}
      {...rest}
    />
  );
};

export default OptimizedImage; 